import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  Plus,
  Search,
  Send,
  CheckCircle,
  Download,
  Copy,
  Ban,
  Trash2,
  Building2,
  X,
  AlertTriangle,
  RefreshCw,
  Pencil,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { InvoiceBilledOrganisations } from "@/components/admin/OrganisationBillingPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/utils/dateFormat";
import { generateUUID } from "@/utils/transform";
import api from "../../services/api";

// Status badge component
function StatusBadge({ status }) {
  const { t } = useTranslation();
  const config = {
    draft: { variant: "secondary", label: t("status.draft", "Draft") },
    sent: {
      variant: "default",
      className:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      label: t("status.sent", "Sent"),
    },
    viewed: { variant: "outline", label: t("status.viewed", "Viewed") },
    paid: {
      variant: "default",
      className:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      label: t("status.paid", "Paid"),
    },
    overdue: { variant: "destructive", label: t("status.overdue", "Overdue") },
    cancelled: {
      variant: "secondary",
      label: t("status.cancelled", "Cancelled"),
    },
  };

  const { variant, className, label } = config[status] || {
    variant: "secondary",
    label: status,
  };

  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  );
}

// Invoice item row component
function InvoiceItemRow({ item, index, onUpdate, onRemove, readOnly }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="flex-1">
        <Input
          type="text"
          value={item.description}
          onChange={(e) =>
            onUpdate(index, { ...item, description: e.target.value })
          }
          placeholder="Description"
          disabled={readOnly}
        />
      </div>
      <div className="w-20">
        <Input
          type="number"
          value={item.quantity}
          onChange={(e) =>
            onUpdate(index, {
              ...item,
              quantity: parseFloat(e.target.value) || 0,
            })
          }
          placeholder="Qty"
          min="0"
          step="1"
          disabled={readOnly}
        />
      </div>
      <div className="w-28">
        <Input
          type="number"
          value={item.unitPrice}
          onChange={(e) =>
            onUpdate(index, {
              ...item,
              unitPrice: parseFloat(e.target.value) || 0,
            })
          }
          placeholder="Price"
          min="0"
          step="0.01"
          disabled={readOnly}
        />
      </div>
      <div className="w-28 py-2 text-right font-medium text-foreground">
        ${(item.quantity * item.unitPrice).toFixed(2)}
      </div>
      {!readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(index)}
          className="text-destructive hover:text-destructive"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// Default payment terms, matching config.billing.paymentTermsDays on the server.
// The due date is prefilled rather than left blank: createInvoice does NOT apply
// payment_terms_days (only the monthly job does), so an empty field produces an
// invoice with no due date that reads "Upon Receipt".
const DEFAULT_PAYMENT_TERMS_DAYS = 14;

// AUD first: every price in platform_pricing and config/plans.js is AUD, and
// selecting anything else relabels the invoice without converting a figure.
const INVOICE_CURRENCIES = [
  { value: "AUD", label: "AUD (A$)" },
  { value: "USD", label: "USD ($)" },
  { value: "NZD", label: "NZD (NZ$)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "EUR", label: "EUR (€)" },
];

const defaultDueDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + DEFAULT_PAYMENT_TERMS_DAYS);
  return d.toISOString().split("T")[0];
};

export const AdminInvoices = () => {
  const queryClient = useQueryClient();

  // Tab state
  const [activeTab, setActiveTab] = useState("invoices");

  // Pagination & filter state (UI-controlled)
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    billingType: "manual",
    organisationId: "",
  });

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  // Invoice id when the create dialog is reused for editing; null when creating.
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [error, setError] = useState(null);
  // Set when "Load usage — every event" runs against an org whose plan already
  // includes an allowance. Stays on screen until the items are cleared or
  // reloaded, so it cannot be missed the way a toast can.
  const [allowanceWarning, setAllowanceWarning] = useState(null);

  // Form state for creating/editing invoice
  const [invoiceForm, setInvoiceForm] = useState({
    organisationId: "",
    plan: "",
    periodStart: "",
    periodEnd: "",
    dueDate: defaultDueDate(),
    taxRate: 0,
    discountAmount: 0,
    discountDescription: "",
    notes: "",
    terms: "Payment due within 14 days of invoice date.",
    currency: "AUD",
    items: [
      { description: "", quantity: 1, unitPrice: 0, itemType: "service" },
    ],
  });

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    method: "bank_transfer",
    reference: "",
  });

  // Memoized query params to prevent unnecessary refetches
  const queryParams = useMemo(
    () => ({
      page,
      limit: 20,
      ...(filters.search && { search: filters.search }),
      ...(filters.status && { status: filters.status }),
      ...(filters.billingType && { billingType: filters.billingType }),
      ...(filters.organisationId && { organisationId: filters.organisationId }),
    }),
    [page, filters],
  );

  // Query: Fetch invoices
  const {
    data: invoicesData,
    isLoading: loading,
    isFetching,
    error: queryError,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: ["admin", "invoices", queryParams],
    queryFn: async () => {
      const params = new URLSearchParams(queryParams);
      const response = await api.get(`/admin/invoices?${params}`);
      return response.data?.data;
    },
    placeholderData: (previousData) => previousData,
  });

  // Query: Fetch organisations for dropdown
  const { data: organisationsData } = useQuery({
    queryKey: ["admin", "organisations", "dropdown"],
    queryFn: async () => {
      const response = await api.get("/admin/organisations?limit=100");
      // `.organisations`, not `.items`: featureController.listOrganizations
      // responds { organisations, pagination }. Reading `.items` yielded an
      // empty array, so the Organization dropdown held nothing but its disabled
      // placeholder and no invoice could be created at all.
      return response.data?.data?.organisations || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract data from queries
  const invoices = invoicesData?.items || [];
  const pagination = invoicesData?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  };
  const organisations = organisationsData || [];

  // Mutation: Create invoice
  const createInvoiceMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post("/admin/invoices", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
      toast.success("Invoice created successfully");
      setShowCreateDialog(false);
      resetForm();
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to create invoice");
    },
  });

  // Mutation: Update an existing invoice. The backend refuses paid and
  // Stripe-managed invoices and recalculates subtotal/tax/total from the items.
  const updateInvoiceMutation = useMutation({
    mutationFn: async ({ invoiceId, data }) => {
      const response = await api.put(`/admin/invoices/${invoiceId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
      toast.success("Invoice updated");
      setShowCreateDialog(false);
      setEditingInvoiceId(null);
      resetForm();
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to update invoice");
    },
  });

  // Mutation: Send invoice
  const sendInvoiceMutation = useMutation({
    mutationFn: async (invoiceId) => {
      const response = await api.post(`/admin/invoices/${invoiceId}/send`);
      return response.data;
    },
    onSuccess: async (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
      toast.success("Invoice sent successfully");
      if (showViewDialog) {
        const response = await api.get(`/admin/invoices/${invoiceId}`);
        setSelectedInvoice(response.data?.data);
      }
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to send invoice");
    },
  });

  // Mutation: Mark as paid
  const markPaidMutation = useMutation({
    mutationFn: async ({ invoiceId, paymentData }) => {
      const response = await api.post(
        `/admin/invoices/${invoiceId}/mark-paid`,
        paymentData,
      );
      return response.data;
    },
    onSuccess: async (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
      toast.success("Payment recorded successfully");
      setShowPaymentDialog(false);
      if (showViewDialog) {
        const response = await api.get(`/admin/invoices/${invoiceId}`);
        setSelectedInvoice(response.data?.data);
      }
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to mark invoice as paid");
    },
  });

  // Mutation: Void invoice (releases its usage back to unbilled)
  const voidInvoiceMutation = useMutation({
    mutationFn: async (invoiceId) => {
      const response = await api.post(`/admin/invoices/${invoiceId}/void`);
      return response.data;
    },
    onSuccess: async (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "usage-log"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "unbilled-usage"] });
      toast.success("Invoice voided — usage released");
      if (showViewDialog) {
        const response = await api.get(`/admin/invoices/${invoiceId}`);
        setSelectedInvoice(response.data?.data);
      }
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to void invoice");
    },
  });

  // Mutation: Duplicate invoice
  const duplicateInvoiceMutation = useMutation({
    mutationFn: async (invoiceId) => {
      const response = await api.post(`/admin/invoices/${invoiceId}/duplicate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
      toast.success("Invoice duplicated successfully");
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to duplicate invoice");
    },
  });

  // Mutation: Delete invoice
  const deleteInvoiceMutation = useMutation({
    mutationFn: async (invoiceId) => {
      const response = await api.delete(`/admin/invoices/${invoiceId}`);
      return response.data;
    },
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] });
      toast.success("Invoice deleted successfully");
      if (showViewDialog && selectedInvoice?.id === invoiceId) {
        setShowViewDialog(false);
        setSelectedInvoice(null);
      }
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to delete invoice");
    },
  });

  // Mutation: Load usage
  const loadUsageMutation = useMutation({
    mutationFn: async ({ organisationId, periodStart, periodEnd }) => {
      // Pull unbilled usage from the Usage Log at the org's configured per-unit
      // prices (per-org override → platform default → code default).
      const response = await api.get(
        `/admin/organisations/${organisationId}/unbilled-invoice-items`,
        { params: { periodStart, periodEnd } },
      );
      return response.data?.data;
    },
    onSuccess: (data) => {
      // Persistent, not a toast: billing a plan customer per-event overcharges
      // them, and a toast that disappears in four seconds is the wrong place
      // to warn about that.
      setAllowanceWarning(
        data?.planAllowance?.hasAllowance ? data.planAllowance : null,
      );

      if (data?.items?.length > 0) {
        setInvoiceForm((prev) => ({
          ...prev,
          items: data.items,
        }));
        toast.success(`Loaded ${data.items.length} usage line item(s)`);
      } else {
        toast("No unbilled usage for this period");
      }
    },
    onError: () => {
      setError("Failed to load usage data");
    },
  });

  // Combined loading state for mutations
  const actionLoading =
    createInvoiceMutation.isPending ||
    sendInvoiceMutation.isPending ||
    markPaidMutation.isPending ||
    duplicateInvoiceMutation.isPending ||
    updateInvoiceMutation.isPending ||
    voidInvoiceMutation.isPending ||
    deleteInvoiceMutation.isPending ||
    loadUsageMutation.isPending;

  // Reset form
  const resetForm = () => {
    // Clear the allowance warning too; it belongs to the items just cleared,
    // and leaving it up would mislabel the next organisation's invoice.
    setAllowanceWarning(null);
    setInvoiceForm({
      organisationId: "",
      plan: "",
      periodStart: "",
      periodEnd: "",
      dueDate: defaultDueDate(),
      taxRate: 0,
      discountAmount: 0,
      discountDescription: "",
      notes: "",
      terms: "Payment due within 14 days of invoice date.",
      currency: "AUD",
      items: [
        { description: "", quantity: 1, unitPrice: 0, itemType: "service" },
      ],
    });
  };

  // Add item row
  const addItem = () => {
    setInvoiceForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { description: "", quantity: 1, unitPrice: 0, itemType: "service" },
      ],
    }));
  };

  // Update item
  const updateItem = (index, item) => {
    setInvoiceForm((prev) => ({
      ...prev,
      items: prev.items.map((i, idx) => (idx === index ? item : i)),
    }));
  };

  // Remove item
  const removeItem = (index) => {
    if (invoiceForm.items.length <= 1) return;
    setInvoiceForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== index),
    }));
  };

  // Calculate totals
  const calculateTotals = (items, taxRate, discountAmount) => {
    const subtotal = items.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0,
    );
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax - discountAmount;
    return { subtotal, tax, total };
  };

  // Load organisation usage
  const loadUsage = () => {
    if (
      !invoiceForm.organisationId ||
      !invoiceForm.periodStart ||
      !invoiceForm.periodEnd
    ) {
      setError("Please select organisation and period dates first");
      return;
    }

    loadUsageMutation.mutate({
      organisationId: invoiceForm.organisationId,
      periodStart: invoiceForm.periodStart,
      periodEnd: invoiceForm.periodEnd,
    });
  };

  // Create invoice
  const handleCreate = () => {
    if (!invoiceForm.organisationId) {
      setError("Please select an organisation");
      return;
    }

    if (
      invoiceForm.items.length === 0 ||
      !invoiceForm.items.some((i) => i.description && i.unitPrice > 0)
    ) {
      setError("Please add at least one valid item");
      return;
    }

    setError(null);
    if (editingInvoiceId) {
      updateInvoiceMutation.mutate({
        invoiceId: editingInvoiceId,
        data: invoiceForm,
      });
      return;
    }
    createInvoiceMutation.mutate(invoiceForm);
  };

  // Edit an existing invoice; reuses the create dialog, pre-filled.
  const handleEdit = async (invoiceId) => {
    setError(null);
    try {
      const response = await api.get(`/admin/invoices/${invoiceId}`);
      const inv = response.data?.data;
      if (!inv) throw new Error("Invoice not found");

      const toInputDate = (value) =>
        value ? new Date(value).toISOString().split("T")[0] : "";

      setInvoiceForm({
        organisationId: inv.organisationId || "",
        plan: inv.plan || "",
        periodStart: toInputDate(inv.periodStart),
        periodEnd: toInputDate(inv.periodEnd),
        dueDate: toInputDate(inv.dueDate),
        taxRate: inv.taxRate ?? 0,
        discountAmount: inv.discountAmount ?? 0,
        discountDescription: inv.discountDescription || "",
        notes: inv.notes || "",
        terms: inv.terms || "",
        currency: inv.currency || "AUD",
        items: (inv.items || []).map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          itemType: item.itemType || "service",
        })),
      });
      setEditingInvoiceId(invoiceId);
      setShowCreateDialog(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load invoice");
    }
  };

  // Send invoice
  const handleSend = (invoiceId) => {
    sendInvoiceMutation.mutate(invoiceId);
  };

  // Mark as paid
  const handleMarkPaid = () => {
    if (!selectedInvoice) return;
    markPaidMutation.mutate({
      invoiceId: selectedInvoice.id,
      paymentData: paymentForm,
    });
  };

  // Duplicate invoice
  const handleDuplicate = (invoiceId) => {
    duplicateInvoiceMutation.mutate(invoiceId);
  };

  // Void invoice (releases its committed usage back to unbilled)
  const handleVoid = (invoiceId) => {
    const msg =
      "Void this invoice? Its usage will be released back to unbilled and can be re-invoiced.";
    if (!confirm(msg)) return;
    voidInvoiceMutation.mutate(invoiceId);
  };

  // Delete invoice
  const handleDelete = (invoiceId) => {
    if (!confirm("Are you sure you want to delete this draft invoice?")) return;
    deleteInvoiceMutation.mutate(invoiceId);
  };

  // Download PDF
  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await api.get(`/admin/invoices/${invoiceId}/pdf`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download PDF");
    }
  };

  // View invoice
  const handleView = async (invoice) => {
    try {
      const response = await api.get(`/admin/invoices/${invoice.id}`);
      setSelectedInvoice(response.data?.data);
      setShowViewDialog(true);
    } catch {
      setError("Failed to load invoice details");
    }
  };

  const totals = calculateTotals(
    invoiceForm.items,
    invoiceForm.taxRate,
    invoiceForm.discountAmount,
  );

  const renderInvoices = () => {
    if (loading) {
      return (
        <div className="p-6 space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }
    return invoices?.length === 0 ? (
      <div className="p-8 text-center">
        <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground mb-2">No invoices found</p>
        <Button
          variant="link"
          onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}
        >
          Create your first invoice
        </Button>
      </div>
    ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice</TableHead>
            <TableHead>Organization</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell>
                <button
                  onClick={() => handleView(invoice)}
                  className="font-medium text-primary hover:underline"
                >
                  {invoice.invoiceNumber}
                </button>
                {invoice.plan && (
                  <p className="text-xs text-muted-foreground">
                    {invoice.plan} plan
                  </p>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>{invoice.organisationName || "Unknown"}</span>
                </div>
              </TableCell>
              <TableCell className="font-medium">
                {invoice.currency} ${invoice.amount?.toFixed(2)}
              </TableCell>
              <TableCell>
                <StatusBadge status={invoice.status} />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {invoice.dueDate
                  ? formatDate(invoice.dueDate)
                  : "-"}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(invoice.createdAt)}
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-1">
                  {invoice.status === "draft" && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(invoice.id)}
                        title="Edit Invoice"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSend(invoice.id)}
                        title="Send Invoice"
                      >
                        <Send className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(invoice.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                  {(invoice.status === "sent" ||
                    invoice.status === "viewed") && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setShowPaymentDialog(true);
                        }}
                        title="Mark as Paid"
                      >
                        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleVoid(invoice.id)}
                        title="Void Invoice (release usage)"
                      >
                        <Ban className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDownloadPDF(invoice.id)}
                    title="Download PDF"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDuplicate(invoice.id)}
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            Invoice Billing
          </h1>
          <p className="text-muted-foreground">
            Manage invoices and invoice-billed customers
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Invoice
        </Button>
      </div>

      {/* Error Alert */}
      {(error || queryError) && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <span className="flex-1">
                {error ||
                  queryError?.response?.data?.message ||
                  "Failed to load invoices. Please ensure the database migration (028_manual_invoices.sql) has been run and the backend server has been restarted."}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setError(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="invoices" className="gap-2">
            <FileText className="h-4 w-4" />
            All Invoices
          </TabsTrigger>
          <TabsTrigger value="customers" className="gap-2">
            <Building2 className="h-4 w-4" />
            Invoice Customers
          </TabsTrigger>
        </TabsList>

        {/* All Invoices Tab */}
        <TabsContent value="invoices" className="mt-6 space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search invoices..."
                      value={filters.search}
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          search: e.target.value,
                        }));
                        setPage(1);
                      }}
                      className="pl-10"
                    />
                  </div>
                </div>

                <Select
                  value={filters.status || "all"}
                  onValueChange={(value) => {
                    setFilters((prev) => ({
                      ...prev,
                      status: value === "all" ? "" : value,
                    }));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-35">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.organisationId || "all"}
                  onValueChange={(value) => {
                    setFilters((prev) => ({
                      ...prev,
                      organisationId: value === "all" ? "" : value,
                    }));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-45">
                    <SelectValue placeholder="Organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organisations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  onClick={() => refetchInvoices()}
                  className="gap-2"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
                  />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Invoices Table */}
          <Card>
            <CardContent className="p-0">
              {renderInvoices()}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="px-4 py-3 border-t border-border flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(page * pagination.limit, pagination.total)} of{" "}
                    {pagination.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((prev) => prev - 1)}
                      disabled={page <= 1 || isFetching}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((prev) => prev + 1)}
                      disabled={page >= pagination.pages || isFetching}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Customers Tab */}
        <TabsContent value="customers" className="mt-6">
          <InvoiceBilledOrganisations
            onCreateInvoice={(data) => {
              // Pre-fill the invoice form with generated items
              setInvoiceForm((prev) => ({
                ...prev,
                organisationId: data.organisationId,
                periodStart: data.periodStart,
                periodEnd: data.periodEnd,
                items: data.items.map((item) => ({
                  description: item.description,
                  quantity: item.quantity,
                  unitPrice: item.unitPrice,
                  itemType: item.category || "service",
                })),
              }));
              setShowCreateDialog(true);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Create Invoice Dialog */}
      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open);
          // Leaving edit mode behind would make the next "Create" silently
          // overwrite the invoice that was last edited.
          if (!open) {
            setEditingInvoiceId(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingInvoiceId ? "Edit Invoice" : "Create Manual Invoice"}
            </DialogTitle>
            <DialogDescription>
              {editingInvoiceId
                ? "Totals are recalculated from the items when you save. Paid invoices cannot be edited."
                : "Create an invoice for an organisation not using Stripe"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Organisation Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  Organization <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={invoiceForm.organisationId || "placeholder"}
                  onValueChange={(value) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      organisationId: value === "placeholder" ? "" : value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>
                      Select organisation
                    </SelectItem>
                    {organisations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Plan</Label>
                <Select
                  value={invoiceForm.plan || "none"}
                  onValueChange={(value) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      plan: value === "none" ? "" : value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select plan (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No plan</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Period and Due Date */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Period Start</Label>
                <DatePicker
                  value={invoiceForm.periodStart}
                  onChange={(e) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      periodStart: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Period End</Label>
                <DatePicker
                  value={invoiceForm.periodEnd}
                  onChange={(e) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      periodEnd: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <DatePicker
                  value={invoiceForm.dueDate}
                  onChange={(e) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {/* Currency. Changing this relabels the invoice; it does NOT
                convert any amount. The figures come from platform_pricing and
                config/plans.js, which are denominated in AUD. */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select
                  value={invoiceForm.currency}
                  onValueChange={(value) =>
                    setInvoiceForm((prev) => ({ ...prev, currency: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INVOICE_CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {invoiceForm.currency !== "AUD" && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Amounts are not converted — platform prices are in AUD.
                  </p>
                )}
              </div>
            </div>

            {/* Load Usage Button.
                This pulls EVERY metered event at its per-unit price and does
                not subtract the plan's included allowance; correct for a
                pay-as-you-go customer, but it would bill a customer on a plan
                for usage their subscription already covers. The Overview tab's
                "Generate items" action is the allowance-aware one. Labelled so
                the choice is deliberate. */}
            <div className="flex flex-col items-end gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadUsage}
                disabled={actionLoading}
                className="gap-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${actionLoading ? "animate-spin" : ""}`}
                />
                Load usage — every event
              </Button>
              <p className="text-xs text-muted-foreground text-right max-w-md">
                Bills every metered event at its per-unit price.{" "}
                <strong>No plan allowance is deducted</strong> — for
                pay-as-you-go customers. For a customer on a plan, use
                &ldquo;Generate items&rdquo; on the Overview tab instead.
              </p>
            </div>

            {/* Plan-allowance warning: only after loading per-event usage for
                an org that has an included allowance. */}
            {allowanceWarning && (
              <div className="flex gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">
                    This organisation is on the {allowanceWarning.plan} plan
                    {allowanceWarning.unlimited
                      ? ", which includes unlimited envelopes."
                      : `, which includes ${allowanceWarning.includedEnvelopes} envelopes per period.`}
                  </p>
                  <p className="mt-1">
                    The items above bill <strong>every</strong> event and do not
                    deduct that allowance, so this invoice would charge for
                    usage the subscription already covers. Use{" "}
                    <strong>Generate items — subscription + overage</strong> on
                    the Overview tab unless this customer is billed purely
                    pay-as-you-go.
                  </p>
                </div>
              </div>
            )}

            {/* Line Items */}
            <div className="space-y-3">
              <Label>
                Invoice Items <span className="text-destructive">*</span>
              </Label>
              <div className="space-y-2">
                <div className="flex gap-3 text-xs text-muted-foreground font-medium">
                  <div className="flex-1">Description</div>
                  <div className="w-20">Quantity</div>
                  <div className="w-28">Unit Price</div>
                  <div className="w-28 text-right">Amount</div>
                  <div className="w-8" />
                </div>
                {invoiceForm.items.map((item, index) => (
                  <InvoiceItemRow
                    key={generateUUID()}
                    item={item}
                    index={index}
                    onUpdate={updateItem}
                    onRemove={removeItem}
                  />
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addItem}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </Button>
            </div>

            {/* Tax and Discount */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  value={invoiceForm.taxRate}
                  onChange={(e) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      taxRate: parseFloat(e.target.value) || 0,
                    }))
                  }
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
              <div className="space-y-2">
                <Label>Discount Amount</Label>
                <Input
                  type="number"
                  value={invoiceForm.discountAmount}
                  onChange={(e) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      discountAmount: parseFloat(e.target.value) || 0,
                    }))
                  }
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Totals */}
            <div className="bg-muted rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="text-foreground">
                  ${totals.subtotal.toFixed(2)}
                </span>
              </div>
              {invoiceForm.taxRate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Tax ({invoiceForm.taxRate}%):
                  </span>
                  <span className="text-foreground">
                    ${totals.tax.toFixed(2)}
                  </span>
                </div>
              )}
              {invoiceForm.discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                  <span>Discount:</span>
                  <span>-${invoiceForm.discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-lg border-t border-border pt-2">
                <span className="text-foreground">Total:</span>
                <span className="text-foreground">
                  {invoiceForm.currency} ${totals.total.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Notes and Terms */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={invoiceForm.notes}
                  onChange={(e) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  rows={3}
                  placeholder="Additional notes for the invoice..."
                />
              </div>
              <div className="space-y-2">
                <Label>Terms</Label>
                <Textarea
                  value={invoiceForm.terms}
                  onChange={(e) =>
                    setInvoiceForm((prev) => ({
                      ...prev,
                      terms: e.target.value,
                    }))
                  }
                  rows={3}
                  placeholder="Payment terms..."
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={actionLoading}>
              {actionLoading && (editingInvoiceId ? "Saving..." : "Creating...")}
              {!actionLoading &&
                (editingInvoiceId ? "Save Changes" : "Create Invoice")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice {selectedInvoice?.invoiceNumber}</DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-6 py-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-muted-foreground">Bill To</p>
                  <p className="font-medium text-foreground">
                    {selectedInvoice.organisationName}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedInvoice.billingEmail}
                  </p>
                </div>
                <StatusBadge status={selectedInvoice.status} />
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="text-foreground">
                    {formatDate(selectedInvoice.createdAt)}
                  </p>
                </div>
                {selectedInvoice.dueDate && (
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="text-foreground">
                      {formatDate(selectedInvoice.dueDate)}
                    </p>
                  </div>
                )}
                {selectedInvoice.paidAt && (
                  <div>
                    <p className="text-muted-foreground">Paid</p>
                    <p className="text-foreground">
                      {formatDate(selectedInvoice.paidAt)}
                    </p>
                  </div>
                )}
              </div>

              {/* Items */}
              <div className="border border-border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInvoice.items?.map((item) => (
                      <TableRow key={generateUUID()}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">
                          {item.quantity}
                        </TableCell>
                        <TableCell className="text-right">
                          ${item.unitPrice?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          ${item.amount?.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="bg-muted p-4 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground">
                      ${selectedInvoice.subtotal?.toFixed(2)}
                    </span>
                  </div>
                  {selectedInvoice.taxAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Tax ({selectedInvoice.taxRate}%)
                      </span>
                      <span className="text-foreground">
                        ${selectedInvoice.taxAmount?.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedInvoice.discountAmount > 0 && (
                    <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                      <span>Discount</span>
                      <span>
                        -${selectedInvoice.discountAmount?.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold pt-2 border-t border-border">
                    <span className="text-foreground">Total</span>
                    <span className="text-foreground">
                      {selectedInvoice.currency} $
                      {selectedInvoice.amount?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm text-foreground">
                    {selectedInvoice.notes}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                {selectedInvoice.status === "draft" && (
                  <>
                    <Button
                      onClick={() => handleSend(selectedInvoice.id)}
                      className="gap-2"
                    >
                      <Send className="h-4 w-4" />
                      Send Invoice
                    </Button>
                    <Button
                      variant="outline"
                      className="text-destructive border-destructive hover:bg-destructive/10 gap-2"
                      onClick={() => handleDelete(selectedInvoice.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </Button>
                  </>
                )}
                {(selectedInvoice.status === "sent" ||
                  selectedInvoice.status === "viewed") && (
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white gap-2"
                    onClick={() => setShowPaymentDialog(true)}
                  >
                    <CheckCircle className="h-4 w-4" />
                    Mark as Paid
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPDF(selectedInvoice.id)}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Mark invoice {selectedInvoice?.invoiceNumber} as paid
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={paymentForm.method}
                onValueChange={(value) =>
                  setPaymentForm((prev) => ({ ...prev, method: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reference Number</Label>
              <Input
                type="text"
                value={paymentForm.reference}
                onChange={(e) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    reference: e.target.value,
                  }))
                }
                placeholder="Transaction ID, check number, etc."
              />
            </div>

            <div className="bg-muted rounded-lg p-3">
              <p className="text-sm text-muted-foreground">
                Amount:{" "}
                <span className="font-semibold text-foreground">
                  {selectedInvoice?.currency} $
                  {selectedInvoice?.amount?.toFixed(2)}
                </span>
              </p>
              {selectedInvoice?.plan && (
                <p className="text-sm text-muted-foreground mt-1">
                  This will also apply the {selectedInvoice.plan} plan to the
                  organisation until{" "}
                  {selectedInvoice.periodEnd
                    ? formatDate(selectedInvoice.periodEnd)
                    : "N/A"}
                  .
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowPaymentDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleMarkPaid}
              disabled={actionLoading}
            >
              {actionLoading ? "Processing..." : "Confirm Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInvoices;
